import { Bot, Star } from "lucide-react"

import { ReviewCard, ReviewCardProps } from "@/components/molecules/Review/card"
import { ReviewButton } from "@/components/molecules/Review/review-button"
import { CarouselNavigation } from "@/components/molecules/Carousel/carousel-navigation"
import { Carousel } from "../Carousel/carousel"
import { CarouselContent } from "../Carousel/carousel-content"
import { cn } from "@/app/utils"

type ReviewsSectionProps = {
  productId: string
  productHandle: string
  reviews: ReviewCardProps[]
  total: number
  avgRating: number | undefined
  summary?: string
  className?: string
  label?: string
  type: "product" | "consultant"
  translations: Record<string, any>
  isOwner?: boolean
  hasReviewed?: boolean
  isLoggedIn?: boolean
}

export const ReviewsSection = ({
  productId,
  productHandle,
  reviews,
  total,
  summary,
  avgRating,
  label,
  type,
  className,
  translations,
  isOwner = false,
  hasReviewed = false,
  isLoggedIn = false,
}: ReviewsSectionProps) => {
  const renderReviewAction = () => {
    if (!isLoggedIn) return <ReviewButton productId={productId} type={type} />
    if (isOwner) {
      return (
        <p className="text-sm font-medium text-gray-500 italic">
          {translations.cannotReviewOwn || "You cannot review your own content"}
        </p>
      )
    }
    if (hasReviewed) {
      return (
        <p className="text-sm font-medium text-blue-600">
          {translations.alreadyReviewed || "You have already reviewed this"}
        </p>
      )
    }
    return <ReviewButton productId={productId} type={type} />
  }

  if (reviews?.length <= 0) {
    return (
      <section className={cn("w-full bg-gray-50 py-12 dark:bg-gray-800 md:my-10")}>
        <div className="mx-auto w-full max-w-screen-xl px-4 md:px-6">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <h2 className="text-xl font-semibold sm:text-2xl">
              {label || "Have this product? Help others by sharing your experience"}
            </h2>
            {renderReviewAction()}
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className={cn("w-full bg-gray-50 py-12 dark:bg-gray-800 md:my-10", className)}>
      <div className="mx-auto w-full max-w-screen-xl px-4 md:px-6">
        <div className="space-y-4">
          {/* Header */}
          <div className="mb-10 flex flex-col items-center justify-between gap-4 md:flex-row">
            <div className="flex items-center justify-center flex-wrap">
              <h2 className="text-xl font-semibold">{productHandle}</h2>
              <span className="ml-2 text-sm text-gray-500">({total})</span>
              {!!avgRating && (
                <div className="ml-2 flex items-center">
                  <Star className="size-4 fill-yellow-400 text-yellow-500" />
                  <span className="ml-1 text-sm">{avgRating.toFixed(2)}</span>
                </div>
              )}
            </div>
            {renderReviewAction()}
          </div>

          {/* AI Summary */}
          {!!summary && (
            <div className="rounded bg-gray-300/25 p-4">
              <div className="mb-2 flex items-center space-x-2">
                <Bot className="size-4" />
                <h3 className="text-base font-semibold">{translations.aiSummary}</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{summary}</p>
            </div>
          )}

          {/* Carousel */}
          <Carousel opts={{ skipSnaps: true }}>
            <div className="my-4 hidden justify-end gap-10 md:flex">
              <CarouselNavigation direction="prev" className="relative" />
              <CarouselNavigation direction="next" className="relative" />
            </div>
            <CarouselContent className="gap-6">
              {reviews.map(({ body, author, rating, createdAt }) => (
                <ReviewCard key={createdAt} body={body} author={author} rating={rating} createdAt={createdAt} />
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </section>
  )
}
